import {
  isActorGlobalScopeOperator,
  isActorSuperAdmin,
  resolveEffectiveBehaviorType,
  type AdminActorProfileOptions,
} from './adminActorContext'

export type PasswordResetTargetRow = {
  userId: number
  roleBehaviorType?: string | null
  roleName?: string | null
}

/** Lower number = higher privilege (matches backend ManagementUserPasswordAuthorizationSupport). */
const HIERARCHY_RANK: Record<string, number> = {
  SUPER_ADMIN: 0,
  COMPANY_GROUP_ADMIN: 2,
  COMPANY_ADMIN: 4,
  SUPERINTENDENT: 4,
  VENDOR: 4,
  SHORE: 4,
  CREW: 5,
  CUSTOM: 99,
}

function targetBehavior(row: PasswordResetTargetRow): string {
  return (row.roleBehaviorType || row.roleName || '').trim().toUpperCase()
}

function hierarchyRank(behavior: string, globalOperator: boolean): number {
  if (behavior === 'OPERATOR' || behavior === 'SUPERINTENDENT') {
    return globalOperator ? 1 : 3
  }
  return HIERARCHY_RANK[behavior] ?? HIERARCHY_RANK.CUSTOM
}

export function isAdminPasswordSelfTarget(
  row: PasswordResetTargetRow,
  actorAppUserId: number | null | undefined
): boolean {
  if (actorAppUserId == null) return false
  return Number(row.userId) === Number(actorAppUserId)
}

export function canShowResetPasswordForTarget(
  currentUser: Record<string, unknown> | null | undefined,
  target: PasswordResetTargetRow,
  opts: {
    canResetPassword: boolean
    actorAppUserId?: number | null
    actorOpts?: AdminActorProfileOptions
  }
): boolean {
  if (!opts.canResetPassword) return false
  if (isAdminPasswordSelfTarget(target, opts.actorAppUserId ?? null)) return false

  const actorBehavior = resolveEffectiveBehaviorType(currentUser, opts.actorOpts)
  const targetType = targetBehavior(target)

  if (targetType === 'SUPER_ADMIN') {
    return isActorSuperAdmin(currentUser, opts.actorOpts)
  }

  if (isActorSuperAdmin(currentUser, opts.actorOpts)) {
    return true
  }

  if (actorBehavior === 'CREW') {
    return false
  }

  const actorGlobalOperator = isActorGlobalScopeOperator(currentUser, opts.actorOpts)
  if (actorGlobalOperator) {
    return true
  }

  const actorRank = hierarchyRank(actorBehavior, false)
  // User list rows are tenant-scoped; treat operator targets as company-scoped unless superadmin.
  const targetRank = hierarchyRank(targetType, false)

  return actorRank < targetRank
}
